import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import Image from '../../../components/AppImage';

const KnowledgeBase = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    { value: 'all', label: 'All Articles' },
    { value: 'tips', label: 'Cleaning Tips' },
    { value: 'seasonal', label: 'Seasonal Guides' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'health', label: 'Health & Safety' },
    { value: 'equipment', label: 'Equipment Care' }
  ];

  const articles = [
    {
      id: 1,
      title: 'Spring Cleaning Checklist: Room by Room Guide',
      category: 'seasonal',
      readTime: '8 min read',
      publishDate: '2025-03-15',
      author: 'Clean360 Team',
      image: 'https://images.pexels.com/photos/4239119/pexels-photo-4239119.jpeg',
      excerpt: 'Get your home sparkling clean this spring with our comprehensive room-by-room cleaning checklist and expert tips.',
      content: `Spring cleaning is more than just a tradition – it's an opportunity to refresh your living space and create a healthier environment for your family. This comprehensive guide will walk you through each room in your home, providing detailed checklists and professional tips to make your spring cleaning efficient and thorough.

## Why Spring Cleaning Matters

Spring cleaning serves multiple purposes beyond just making your home look good. It helps remove allergens that have built up over winter, improves indoor air quality, and creates a fresh start for the warmer months ahead. Studies show that a clean, organized environment can reduce stress and improve mental clarity.

## Getting Started: Essential Supplies

Before diving into specific rooms, gather these essential cleaning supplies:
- All-purpose cleaner
- Glass cleaner
- Disinfectant
- Microfiber cloths
- Vacuum cleaner with attachments
- Mop and bucket
- Scrub brushes
- Rubber gloves
- Trash bags
- Storage containers

## Kitchen Deep Clean

The kitchen requires special attention due to food preparation and cooking residue:

**Appliances:**
- Clean inside and outside of refrigerator, removing expired items
- Deep clean oven, including racks and drip pans
- Descale coffee maker and clean small appliances
- Wipe down microwave inside and out

**Surfaces and Storage:**
- Empty and clean all cabinets and drawers
- Organize pantry and check expiration dates
- Clean countertops and backsplash thoroughly
- Sanitize cutting boards and replace if heavily worn

## Living Areas and Bedrooms

Focus on areas where family spends the most time:

**Furniture and Decor:**
- Dust all surfaces, including baseboards and crown molding
- Clean upholstery and vacuum under cushions
- Wash or dry clean curtains and blinds
- Rotate mattresses and wash bedding in hot water

**Electronics and Books:**
- Dust electronics and organize cables
- Clean screens with appropriate cleaners
- Organize books and donate unused items
- Vacuum or clean air vents

## Bathrooms: Deep Sanitization

Bathrooms require thorough disinfection:

**Fixtures and Surfaces:**
- Scrub tile grout and re-caulk if necessary
- Deep clean toilet, including behind and around base
- Descale showerheads and faucets
- Clean and organize medicine cabinets

**Ventilation and Moisture Control:**
- Clean exhaust fans and replace filters
- Check for mold or mildew and address immediately
- Wash shower curtains or clean glass doors
- Replace old towels and bath mats

## Seasonal Maintenance Tasks

Don't forget these important seasonal tasks:

**HVAC System:**
- Replace air filters
- Clean air ducts if necessary
- Test heating and cooling systems
- Clean dryer vents for safety

**Windows and Doors:**
- Wash windows inside and out
- Clean window screens
- Check and clean door frames
- Lubricate hinges and locks

## Organization and Decluttering

Spring cleaning is the perfect time to declutter:

**The Three-Box Method:**
- Keep: Items you use regularly
- Donate: Items in good condition you don't need
- Trash: Broken or worn-out items

**Storage Solutions:**
- Invest in clear storage containers
- Label everything clearly
- Use vertical space efficiently
- Create designated homes for frequently used items

## Professional Tips for Success

**Time Management:**
- Break tasks into manageable chunks
- Start with one room at a time
- Set realistic daily goals
- Reward yourself for completed tasks

**Efficiency Strategies:**
- Work from top to bottom in each room
- Use the right tools for each job
- Clean as you go to maintain progress
- Involve family members with age-appropriate tasks

## Maintaining Your Clean Home

Once your spring cleaning is complete, maintain your efforts:

**Daily Habits:**
- Make beds every morning
- Do dishes after each meal
- Wipe down surfaces after use
- Put items back in their designated places

**Weekly Routines:**
- Vacuum high-traffic areas
- Clean bathrooms thoroughly
- Change bed linens
- Take out trash and recycling

## When to Call Professionals

Consider professional help for:
- Deep carpet cleaning
- Window washing on upper floors
- HVAC system maintenance
- Specialized stain removal
- Heavy-duty tasks requiring special equipment

Spring cleaning doesn't have to be overwhelming. By following this systematic approach and taking it one room at a time, you'll have a fresh, clean home that's ready for the season ahead. Remember, the goal is progress, not perfection – every step you take makes a difference in creating a healthier, more comfortable living environment.`,
      tags: ['spring cleaning', 'checklist', 'organization', 'deep cleaning']
    },
    {
      id: 2,
      title: 'The Ultimate Guide to Carpet Care and Maintenance',
      category: 'maintenance',
      readTime: '12 min read',
      publishDate: '2025-03-10',
      author: 'Sarah Johnson',
      image: 'https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg',
      excerpt: 'Learn professional carpet care techniques to extend the life of your carpets and keep them looking fresh and clean.',
      content: `Proper carpet care is essential for maintaining a healthy home environment and protecting your investment. Quality carpets can last for decades with proper maintenance, while neglected carpets may need replacement in just a few years. This comprehensive guide covers everything you need to know about carpet care, from daily maintenance to professional cleaning.

## Understanding Your Carpet

Different carpet types require different care approaches:

**Fiber Types:**
- **Nylon:** Durable and stain-resistant, responds well to most cleaning methods
- **Polyester:** Stain-resistant but can be oil-attracting, requires gentle cleaning
- **Wool:** Natural fiber that's durable but sensitive to alkaline cleaners
- **Polypropylene:** Very stain-resistant but can be damaged by heat

**Construction Types:**
- **Cut pile:** Soft and comfortable, shows footprints and vacuum marks
- **Loop pile:** Durable and hides soil well, but can snag
- **Cut and loop:** Combines benefits of both, creates textural patterns

## Daily and Weekly Maintenance

**Vacuuming Best Practices:**
- Vacuum high-traffic areas daily
- Vacuum entire carpet at least twice weekly
- Use slow, overlapping strokes for best soil removal
- Empty vacuum bag or canister when half full
- Replace vacuum filters regularly

**Immediate Spill Response:**
- Blot spills immediately with clean, white cloth
- Work from outside of spill toward center
- Never rub or scrub, which can damage fibers
- Use appropriate cleaning solution for spill type
- Rinse with clean water and blot dry

## Stain Prevention Strategies

**Protective Measures:**
- Apply carpet protector after professional cleaning
- Use area rugs in high-traffic zones
- Remove shoes at entrances
- Rotate furniture periodically to prevent permanent indentations

**Traffic Pattern Management:**
- Use runners in hallways
- Rearrange furniture occasionally
- Consider carpet tiles in heavy-use areas
- Install quality door mats inside and outside

## DIY Cleaning Techniques

**Spot Cleaning Solutions:**

*For Water-Soluble Stains:*
- Mix 1/4 teaspoon liquid dish soap with 1 cup warm water
- Apply with clean cloth, blot, rinse, and dry

*For Grease Stains:*
- Apply dry cleaning solvent to clean cloth
- Blot stain, working from outside in
- Follow with water-based cleaner if needed

*For Protein Stains (blood, sweat):*
- Use enzyme cleaner specifically designed for proteins
- Follow product instructions carefully
- Rinse thoroughly and dry completely

**Deep Cleaning Methods:**

*Steam Cleaning (Hot Water Extraction):*
- Rent or purchase quality steam cleaner
- Pre-treat stains and high-traffic areas
- Use manufacturer-recommended cleaning solution
- Make slow passes, allowing machine to extract maximum water
- Ensure adequate ventilation for faster drying

*Dry Cleaning:*
- Apply dry cleaning compound according to instructions
- Work into carpet with brush or machine
- Allow dwell time as specified
- Vacuum thoroughly to remove all residue

## Professional Cleaning Guidelines

**When to Call Professionals:**
- Every 12-18 months for average households
- Every 6-12 months for high-traffic homes
- Immediately for large spills or flooding
- When DIY methods aren't effective
- Before stains set permanently

**Choosing a Professional Service:**
- Verify insurance and bonding
- Check references and online reviews
- Ensure technicians are certified (IICRC preferred)
- Get written estimates and service guarantees
- Ask about their cleaning process and equipment

**Professional Cleaning Methods:**

*Hot Water Extraction:*
- Most effective deep cleaning method
- Uses heated water and powerful suction
- Removes deep-seated soil and allergens
- Requires 6-12 hours drying time

*Dry Cleaning:*
- Uses minimal moisture
- Faster drying time (1-2 hours)
- Good for delicate fibers
- May not remove deep soil as effectively

*Bonnet Cleaning:*
- Surface cleaning method
- Quick drying
- Good for maintenance between deep cleanings
- Not suitable as primary cleaning method

## Dealing with Common Problems

**Pet Odors and Stains:**
- Clean accidents immediately
- Use enzyme cleaners designed for pet stains
- Consider professional treatment for set-in odors
- Address subfloor contamination if necessary

**High-Traffic Wear:**
- Rotate furniture to redistribute traffic
- Use area rugs and runners
- Consider professional restoration for severe wear
- Plan for eventual replacement in heavily worn areas

**Carpet Beetles and Moths:**
- Vacuum regularly, including edges and under furniture
- Use appropriate insecticides if infestation occurs
- Consider professional pest control for severe problems
- Store wool items properly to prevent damage

## Extending Carpet Life

**Environmental Factors:**
- Maintain consistent humidity (30-50%)
- Avoid excessive heat and direct sunlight
- Ensure proper ventilation
- Use air purifiers to reduce airborne soil

**Furniture Protection:**
- Use furniture pads under heavy pieces
- Avoid dragging furniture across carpet
- Rotate furniture placement periodically
- Consider professional stretching if carpet loosens

## Health and Safety Considerations

**Indoor Air Quality:**
- Regular vacuuming removes allergens and dust mites
- Professional cleaning eliminates deep-seated contaminants
- Address moisture issues immediately to prevent mold
- Use low-VOC cleaning products when possible

**Chemical Safety:**
- Always test cleaning products in inconspicuous areas
- Ensure adequate ventilation during cleaning
- Keep cleaning products away from children and pets
- Follow manufacturer instructions exactly

## Cost-Effective Maintenance

**Budget-Friendly Tips:**
- Invest in quality door mats
- Address spills immediately to prevent staining
- Rotate area rugs to distribute wear
- Maintain vacuum cleaner for optimal performance

**When to Replace:**
- Carpet shows significant wear or matting
- Stains or odors cannot be removed
- Padding has deteriorated
- Carpet no longer matches home's aesthetic

## Seasonal Carpet Care

**Spring:**
- Deep clean to remove winter salt and grime
- Address any moisture damage from winter
- Apply carpet protector after cleaning

**Summer:**
- Increase vacuuming frequency due to open windows
- Address pet shedding with more frequent cleaning
- Monitor for insect activity

**Fall:**
- Prepare for increased indoor time
- Clean before holiday entertaining
- Check and replace door mats

**Winter:**
- Address salt stains promptly
- Maintain proper humidity levels
- Increase vacuuming in entryways

Proper carpet care requires consistent effort but pays dividends in extended carpet life, improved indoor air quality, and a more attractive home environment. By following these guidelines and establishing regular maintenance routines, you can keep your carpets looking and performing their best for years to come.`,
      tags: ['carpet care', 'maintenance', 'stain removal', 'professional cleaning']
    },
    {
      id: 3,
      title: '10 Natural Cleaning Products You Can Make at Home',category: 'tips',readTime: '6 min read',publishDate: '2025-03-08',author: 'Mike Chen',image: 'https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg',excerpt: 'Discover simple, effective, and eco-friendly cleaning solutions you can make with common household ingredients.',
      content: `Making your own cleaning products is not only cost-effective but also allows you to control exactly what goes into the products you use in your home. These natural alternatives are safer for your family, pets, and the environment while being just as effective as commercial cleaners.

## Benefits of Homemade Cleaners

**Health Advantages:**
- No harsh chemicals or toxic fumes
- Reduced risk of allergic reactions
- Safer for children and pets
- Better indoor air quality

**Environmental Benefits:**
- Biodegradable ingredients
- Reduced plastic packaging waste
- Lower carbon footprint
- No harmful chemicals entering water systems

**Economic Advantages:**
- Significantly lower cost per use
- Bulk ingredient purchasing saves money
- Multi-purpose solutions reduce product variety
- Long shelf life of basic ingredients

## Essential Ingredients and Their Properties

**White Vinegar:**
- Natural disinfectant and deodorizer
- Cuts through grease and soap scum
- Safe for most surfaces (avoid natural stone)
- Antimicrobial properties

**Baking Soda:**
- Gentle abrasive for scrubbing
- Natural deodorizer
- Helps remove stains
- Safe for food preparation areas

**Castile Soap:**
- Plant-based, biodegradable
- Gentle yet effective cleaner
- Suitable for sensitive skin
- Versatile base for many recipes

**Essential Oils:**
- Natural fragrance
- Some have antimicrobial properties
- Tea tree, lavender, and lemon are popular choices
- Use sparingly for best results

## 10 Essential Homemade Cleaners

### 1. All-Purpose Cleaner
**Ingredients:**
- 2 cups water
- 1/2 cup white vinegar
- 1/4 cup rubbing alcohol
- 1-2 drops dish soap
- 10-15 drops essential oil (optional)

**Instructions:**
Mix all ingredients in a spray bottle. Shake before each use. Safe for most surfaces including countertops, appliances, and glass.

### 2. Glass and Mirror Cleaner
**Ingredients:**
- 2 cups water
- 1/2 cup white vinegar
- 1/4 cup rubbing alcohol

**Instructions:**
Combine in spray bottle. Spray on surface and wipe with lint-free cloth or newspaper for streak-free results.

### 3. Bathroom Scrub
**Ingredients:**
- 1 cup baking soda
- 1/4 cup liquid castile soap
- 1 tablespoon water
- 10 drops tea tree oil

**Instructions:**
Mix into paste consistency. Apply to surfaces, scrub gently, and rinse thoroughly. Excellent for tubs, tiles, and sinks.

### 4. Toilet Bowl Cleaner
**Ingredients:**
- 1/2 cup baking soda
- 1/4 cup white vinegar
- 10 drops essential oil

**Instructions:**
Sprinkle baking soda in bowl, add vinegar and essential oil. Let sit for 15 minutes, scrub, and flush.

### 5. Floor Cleaner
**Ingredients:**
- 1/2 cup white vinegar
- 1 gallon warm water
- 2-3 drops liquid dish soap

**Instructions:**
Mix in mop bucket. Safe for most hard floors. Test in inconspicuous area first. Avoid on natural stone floors.

### 6. Furniture Polish
**Ingredients:**
- 1/4 cup olive oil
- 2 tablespoons white vinegar
- 10 drops lemon essential oil

**Instructions:**
Mix in small bowl. Apply small amount to soft cloth and polish wood furniture. Buff to shine.

### 7. Carpet Deodorizer
**Ingredients:**
- 1 cup baking soda
- 20 drops essential oil of choice

**Instructions:**
Mix thoroughly and store in shaker container. Sprinkle on carpet, let sit 15 minutes, then vacuum.

### 8. Oven Cleaner
**Ingredients:**
- 1/2 cup baking soda
- 3 tablespoons water
- White vinegar in spray bottle

**Instructions:**
Make paste with baking soda and water. Spread on oven surfaces, let sit overnight. Spray with vinegar and scrub clean.

### 9. Stainless Steel Cleaner
**Ingredients:**
- 1/4 cup white vinegar
- 2-3 drops olive oil

**Instructions:**
Apply vinegar with soft cloth, then buff with small amount of olive oil for shine. Always wipe in direction of grain.

### 10. Laundry Detergent
**Ingredients:**
- 1 cup washing soda
- 1 cup borax
- 1 bar grated castile soap
- 20 drops essential oil

**Instructions:**
Mix all ingredients thoroughly. Use 1-2 tablespoons per load. Store in airtight container.

## Safety Tips and Precautions

**General Safety:**
- Always label homemade cleaners clearly
- Test on inconspicuous areas first
- Never mix different cleaning products
- Store away from children and pets
- Use within 6 months for best effectiveness

**Specific Warnings:**
- Don't use vinegar on natural stone (marble, granite)
- Avoid castile soap in hard water (may leave residue)
- Essential oils can be toxic to pets in large quantities
- Some people may be sensitive to certain ingredients

## Storage and Shelf Life

**Proper Storage:**
- Use glass or high-quality plastic containers
- Label with contents and date made
- Store in cool, dry place away from sunlight
- Keep spray bottles clean to prevent clogging

**Shelf Life Guidelines:**
- Vinegar-based cleaners: 6 months
- Oil-based cleaners: 3 months
- Baking soda mixtures: Use immediately or store dry ingredients separately
- Castile soap solutions: 1 month

## Troubleshooting Common Issues

**Streaky Glass Cleaner:**
- Reduce vinegar concentration
- Ensure cloth is lint-free
- Clean in circular motions, finish with vertical strokes

**Soap Scum on Surfaces:**
- Increase vinegar concentration
- Let cleaner sit longer before wiping
- Use soft scrub brush for textured surfaces

**Strong Vinegar Smell:**
- Add more essential oil
- Ensure good ventilation while cleaning
- Smell dissipates quickly as vinegar dries

## Cost Comparison

**Homemade vs. Commercial:**
- All-purpose cleaner: $0.10 vs. $3.00 per bottle
- Glass cleaner: $0.08 vs. $2.50 per bottle
- Bathroom scrub: $0.15 vs. $4.00 per container
- Annual savings: $200-400 for average household

## Advanced Recipes and Variations

**Heavy-Duty Degreaser:**
Add 2 tablespoons of washing soda to basic all-purpose cleaner recipe for extra grease-cutting power.

**Disinfecting Cleaner:**
Increase rubbing alcohol to 1/2 cup in all-purpose recipe for enhanced antimicrobial action.

**Seasonal Variations:**
- Spring: Add lemon essential oil for fresh scent
- Summer: Use peppermint oil for cooling effect
- Fall: Try orange or cinnamon scents
- Winter: Use eucalyptus for respiratory benefits

Making your own cleaning products is a rewarding way to create a healthier home environment while saving money. Start with one or two basic recipes and gradually expand your homemade cleaning arsenal as you become more comfortable with the process. Your family, wallet, and the environment will thank you.`,
      tags: ['DIY', 'natural cleaning', 'eco-friendly', 'homemade cleaners']
    },
    {
      id: 4,
      title: 'Indoor Air Quality: How Cleaning Affects Your Health',
      category: 'health',
      readTime: '10 min read',
      publishDate: '2025-03-05',
      author: 'Dr. Lisa Martinez',
      image: 'https://images.pexels.com/photos/4239119/pexels-photo-4239119.jpeg',
      excerpt: 'Understand the connection between cleaning practices and indoor air quality, and learn how to create a healthier home environment.',
      content: `Indoor air quality has a profound impact on our health and well-being, yet it's often overlooked in discussions about healthy living. The air inside our homes can be 2-5 times more polluted than outdoor air, making proper cleaning and maintenance crucial for creating a healthy living environment.

## Understanding Indoor Air Pollutants

**Common Indoor Pollutants:**

*Particulate Matter:*
- Dust mites and their waste products
- Pet dander and hair
- Pollen brought in from outdoors
- Dead skin cells
- Fabric fibers and lint

*Chemical Pollutants:*
- Volatile Organic Compounds (VOCs) from cleaning products
- Formaldehyde from furniture and building materials
- Pesticide residues
- Tobacco smoke
- Cooking fumes and grease particles

*Biological Contaminants:*
- Mold and mildew spores
- Bacteria and viruses
- Dust mites
- Pet allergens
- Pollen

## Health Effects of Poor Indoor Air Quality

**Immediate Effects:**
- Eye, nose, and throat irritation
- Headaches and dizziness
- Fatigue and difficulty concentrating
- Respiratory symptoms like coughing and sneezing
- Skin irritation

**Long-term Effects:**
- Asthma development or worsening
- Allergic sensitization
- Respiratory infections
- Cardiovascular problems
- Potential cancer risk from certain chemicals

**Vulnerable Populations:**
- Children (developing respiratory systems)
- Elderly individuals
- People with existing respiratory conditions
- Pregnant women
- Individuals with compromised immune systems

## How Cleaning Improves Air Quality

**Dust and Allergen Removal:**
Regular vacuuming and dusting removes particles that would otherwise become airborne. HEPA-filtered vacuums are particularly effective at capturing small particles that can trigger allergies and asthma.

**Moisture Control:**
Proper cleaning prevents mold and mildew growth by controlling moisture levels. This includes:
- Promptly cleaning up spills
- Ensuring adequate ventilation in bathrooms and kitchens
- Regular cleaning of areas prone to moisture buildup

**Chemical Reduction:**
Using natural or low-VOC cleaning products reduces the introduction of harmful chemicals into your indoor environment.

## Effective Cleaning Strategies for Better Air Quality

### 1. Comprehensive Dust Management

**High-Efficiency Vacuuming:**
- Use HEPA-filtered vacuum cleaners
- Vacuum carpets and rugs at least twice weekly
- Don't forget upholstered furniture and curtains
- Use appropriate attachments for different surfaces

**Proper Dusting Techniques:**
- Use microfiber cloths that trap rather than spread dust
- Work from top to bottom in each room
- Don't forget often-missed areas like ceiling fans and light fixtures
- Wash dusting cloths regularly in hot water

### 2. Humidity and Moisture Control

**Optimal Humidity Levels:**
- Maintain humidity between 30-50%
- Use dehumidifiers in damp areas
- Ensure proper ventilation in bathrooms and kitchens
- Fix leaks promptly to prevent mold growth

**Mold Prevention:**
- Clean bathroom surfaces regularly with mold-preventing cleaners
- Ensure adequate ventilation during and after showers
- Check and clean HVAC systems regularly
- Address any water damage immediately

### 3. Air Filtration and Circulation

**HVAC System Maintenance:**
- Change air filters every 1-3 months
- Use high-quality filters (MERV 8-13 for most homes)
- Have ductwork cleaned professionally every 3-5 years
- Ensure proper system maintenance

**Natural Ventilation:**
- Open windows when outdoor air quality is good
- Use exhaust fans in bathrooms and kitchens
- Create cross-ventilation when possible
- Consider whole-house ventilation systems

## Choosing Air Quality-Friendly Cleaning Products

**Low-VOC and Natural Options:**
- Look for products certified by Green Seal or EPA Safer Choice
- Choose fragrance-free options when possible
- Consider making your own cleaners with simple ingredients
- Avoid aerosol products that can disperse particles

**Reading Labels:**
- Avoid products with warnings like "use in well-ventilated areas"
- Look for specific ingredient lists rather than vague terms
- Choose concentrated products to reduce packaging and transportation impact
- Consider refillable options

## Room-by-Room Air Quality Strategies

### Kitchen
**Key Focus Areas:**
- Range hood maintenance and use
- Regular cleaning of grease buildup
- Proper food storage to prevent pest attraction
- Adequate ventilation during cooking

**Specific Actions:**
- Clean range hood filters monthly
- Wipe down surfaces after cooking
- Store food in sealed containers
- Use exhaust fans during and after cooking

### Bedroom
**Key Focus Areas:**
- Bedding and mattress hygiene
- Dust mite control
- Air circulation
- Chemical exposure reduction

**Specific Actions:**
- Wash bedding weekly in hot water (130°F+)
- Use dust mite-proof mattress and pillow covers
- Vacuum mattresses regularly
- Minimize use of air fresheners and scented products

### Bathroom
**Key Focus Areas:**
- Mold and mildew prevention
- Humidity control
- Ventilation
- Chemical exposure from cleaning products

**Specific Actions:**
- Run exhaust fan during and after showers
- Clean surfaces regularly to prevent mold growth
- Use natural cleaning products when possible
- Keep surfaces dry between uses

### Living Areas
**Key Focus Areas:**
- Dust control on furniture and electronics
- Pet dander management
- Air circulation
- Chemical off-gassing from furniture

**Specific Actions:**
- Dust electronics and furniture weekly
- Groom pets regularly and wash pet bedding frequently
- Use air purifiers in high-use areas
- Choose low-VOC furniture and materials

## Technology and Tools for Better Air Quality

**Air Purifiers:**
- HEPA filters for particle removal
- Activated carbon for chemical absorption
- UV-C light for biological contaminant control
- Size appropriately for room dimensions

**Monitoring Tools:**
- Indoor air quality monitors
- Humidity gauges
- CO2 monitors for ventilation assessment
- Smart thermostats with air quality features

**Professional Services:**
- Air duct cleaning
- Mold inspection and remediation
- HVAC system optimization
- Indoor air quality assessments

## Creating an Action Plan

**Daily Habits:**
- Make beds to reduce dust mite habitat
- Wipe down kitchen surfaces after use
- Use exhaust fans during cooking and bathing
- Keep humidity levels in check

**Weekly Tasks:**
- Vacuum carpets and rugs thoroughly
- Dust all surfaces with microfiber cloths
- Wash bedding in hot water
- Clean bathroom surfaces to prevent mold

**Monthly Maintenance:**
- Change HVAC filters
- Deep clean areas prone to moisture buildup
- Check and clean exhaust fans
- Assess and address any air quality concerns

**Seasonal Activities:**
- Professional duct cleaning (as needed)
- Deep clean carpets and upholstery
- Service HVAC systems
- Address any moisture or mold issues

## Special Considerations

**For Allergy Sufferers:**
- Use allergen-proof bedding covers
- Consider removing carpets in bedrooms
- Keep pets out of bedrooms
- Use HEPA air purifiers

**For Asthma Management:**
- Avoid strong fragrances and chemicals
- Maintain consistent humidity levels
- Keep rescue medications accessible during cleaning
- Consider professional cleaning for deep sanitization

**For Chemical Sensitivities:**
- Use only natural, fragrance-free products
- Ensure excellent ventilation during cleaning
- Test new products in small areas first
- Consider professional consultation for severe sensitivities

The connection between cleaning practices and indoor air quality is undeniable. By implementing comprehensive cleaning strategies focused on air quality improvement, you can create a healthier home environment that supports the well-being of all occupants. Remember that consistency is key – regular maintenance and attention to air quality factors will yield the best results for your family's health.`,
      tags: ['indoor air quality', 'health', 'allergens', 'HVAC maintenance']
    },
    {
      id: 5,
      title: 'Winter Cleaning: Preparing Your Home for Cold Weather',
      category: 'seasonal',
      readTime: '7 min read',
      publishDate: '2025-02-28',
      author: 'Clean360 Team',
      image: 'https://images.pexels.com/photos/1029896/pexels-photo-1029896.jpeg',
      excerpt: 'Get your home ready for winter with essential cleaning and maintenance tasks that will keep your family comfortable and healthy.',
      content: `Winter preparation goes beyond just checking your heating system and weatherproofing windows. Proper cleaning and maintenance before cold weather arrives can prevent problems, improve indoor air quality, and ensure your home remains comfortable throughout the winter months.

## Why Winter Cleaning Matters

**Health Benefits:**
- Removes allergens that accumulate during closed-window months
- Prevents mold growth in areas with poor ventilation
- Reduces dust buildup that can aggravate respiratory conditions
- Creates a cleaner environment for increased indoor time

**Energy Efficiency:**
- Clean HVAC systems operate more efficiently
- Proper insulation and sealing reduce energy costs
- Clean windows maximize natural light during shorter days
- Well-maintained appliances use less energy

**Preventive Maintenance:**
- Identifies potential problems before they become costly repairs
- Prevents pest infestations during their search for warm shelter
- Protects surfaces from salt and moisture damage
- Extends the life of home systems and furnishings

## Essential Pre-Winter Cleaning Tasks

### HVAC System Preparation

**Heating System Maintenance:**
- Replace or clean air filters
- Have professional inspection and tune-up
- Clean air vents and registers throughout the home
- Test thermostat operation and replace batteries
- Inspect and clean ductwork if necessary

**Fireplace and Chimney:**
- Professional chimney inspection and cleaning
- Check fireplace screen and tools
- Stock seasoned firewood in dry location
- Test smoke and carbon monoxide detectors
- Clean glass doors and surrounding area

### Window and Door Maintenance

**Window Preparation:**
- Clean windows inside and out for maximum light
- Check and replace weatherstripping
- Caulk any gaps around window frames
- Install storm windows or plastic sheeting if needed
- Clean and lubricate window hardware

**Door Maintenance:**
- Deep clean entry doors and frames
- Replace or adjust weatherstripping
- Check door sweeps and thresholds
- Lubricate hinges and locks
- Install or maintain storm doors

### Gutter and Exterior Cleaning

**Gutter System:**
- Clean gutters and downspouts thoroughly
- Check for proper drainage away from foundation
- Repair any loose or damaged sections
- Install gutter guards if beneficial
- Ensure proper slope for water flow

**Exterior Surfaces:**
- Power wash siding, decks, and walkways
- Clean and seal outdoor furniture before storage
- Remove leaves and debris from around foundation
- Trim vegetation away from house
- Check and clean exterior light fixtures

## Room-by-Room Winter Preparation

### Kitchen
**Deep Cleaning Tasks:**
- Clean refrigerator coils for efficient operation
- Deep clean oven and stovetop for holiday cooking
- Organize pantry and check expiration dates
- Clean and organize spice rack
- Descale coffee makers and small appliances

**Winter-Specific Preparations:**
- Stock up on non-perishable foods
- Ensure adequate lighting for shorter days
- Check that exhaust fan is working properly
- Clean and organize baking supplies for holiday cooking

### Bathrooms
**Moisture Control:**
- Deep clean all surfaces to prevent mold growth
- Check and clean exhaust fans
- Re-caulk tubs and showers if needed
- Clean and organize medicine cabinets
- Check plumbing for leaks

**Winter Comfort:**
- Add extra towels and bath mats
- Check heating in bathroom areas
- Ensure adequate ventilation to prevent condensation
- Stock up on personal care items

### Bedrooms
**Bedding and Textiles:**
- Switch to warmer bedding and blankets
- Clean and store summer clothing
- Organize winter clothing and accessories
- Deep clean carpets and rugs
- Wash curtains and drapes

**Air Quality:**
- Clean air vents and replace filters
- Dust all surfaces thoroughly
- Vacuum mattresses and box springs
- Consider using humidifiers for dry winter air

### Living Areas
**Furniture and Decor:**
- Deep clean upholstered furniture
- Dust and polish wood furniture
- Clean and organize entertainment centers
- Prepare fireplace area for use
- Rearrange furniture for optimal heating efficiency

**Lighting and Ambiance:**
- Clean light fixtures and replace bulbs
- Add task lighting for shorter days
- Clean mirrors and glass surfaces
- Organize books and magazines for indoor entertainment

## Basement and Attic Preparation

### Basement Winterization
**Moisture Control:**
- Check for leaks and address immediately
- Clean and test sump pump if applicable
- Ensure proper ventilation
- Remove any stored items from floor level
- Check foundation for cracks or damage

**Storage Organization:**
- Organize holiday decorations for easy access
- Protect stored items from moisture
- Check stored clothing and linens for pests
- Ensure clear pathways for safety

### Attic Maintenance
**Insulation and Ventilation:**
- Check insulation levels and add if needed
- Ensure proper ventilation to prevent ice dams
- Seal any air leaks around fixtures
- Check for pest entry points
- Organize stored items safely

## Winter Cleaning Supply Preparation

**Essential Winter Supplies:**
- Rock salt or ice melt for walkways
- Snow removal tools
- Extra batteries for flashlights
- Candles and matches for power outages
- First aid supplies

**Cleaning Product Adjustments:**
- Stock up on disinfectants for cold and flu season
- Have carpet cleaners ready for salt stains
- Prepare natural de-icing solutions
- Ensure adequate supply of paper towels and tissues

## Indoor Air Quality for Winter

**Humidity Management:**
- Use humidifiers to combat dry winter air
- Monitor humidity levels (30-50% ideal)
- Ensure proper ventilation despite closed windows
- Consider air purifiers for improved air quality

**Ventilation Strategies:**
- Use bathroom and kitchen exhaust fans
- Open windows briefly on mild days for fresh air
- Ensure HVAC system provides adequate air circulation
- Consider heat recovery ventilators for energy efficiency

## Preventing Winter-Specific Problems

**Salt and Moisture Damage:**
- Place quality mats at all entrances
- Create designated areas for wet boots and coats
- Protect floors near entrances with runners
- Have cleaning supplies ready for salt stains

**Pest Prevention:**
- Seal cracks and entry points
- Remove food sources that attract pests
- Check stored items regularly
- Consider professional pest control if needed

**Mold and Mildew Prevention:**
- Maintain proper humidity levels
- Ensure adequate ventilation in all areas
- Address any moisture problems immediately
- Clean areas prone to condensation regularly

## Emergency Preparedness

**Power Outage Preparation:**
- Clean and test emergency lighting
- Organize emergency supplies in accessible location
- Ensure battery-powered radio is functional
- Stock non-perishable food and water

**Heating System Backup:**
- Have alternative heating sources ready
- Ensure proper ventilation for any backup heating
- Stock extra blankets and warm clothing
- Know how to shut off utilities if necessary

## Maintenance Schedule

**Weekly Tasks:**
- Check and change air filters as needed
- Clean high-traffic areas more frequently
- Monitor humidity levels
- Clear snow and ice from walkways

**Monthly Tasks:**
- Deep clean one room thoroughly
- Check heating system operation
- Inspect for any winter damage
- Organize and declutter storage areas

**As-Needed Tasks:**
- Address any leaks or moisture problems immediately
- Clean up salt stains promptly
- Remove snow buildup from roof if excessive
- Check and maintain emergency supplies

Winter cleaning and preparation require a systematic approach, but the effort pays off in comfort, health, and peace of mind throughout the cold months. By addressing these tasks before winter arrives, you'll create a warm, clean, and safe environment for your family while potentially saving money on energy costs and preventing costly repairs. Remember that consistency in maintenance is key to preventing problems and ensuring your home remains a comfortable refuge during the winter season.`,
      tags: ['winter preparation', 'seasonal cleaning', 'HVAC maintenance', 'home maintenance']
    }
  ];

  const filteredArticles = articles?.filter(article => {
    const matchesSearch = article?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         article?.excerpt?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         article?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || article?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
  };

  const closeArticleDetail = () => {
    setSelectedArticle(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatContent = (content) => {
    // Simple markdown-like formatting
    return content?.split('\n\n')?.map((paragraph, index) => {
        if (paragraph?.startsWith('## ')) {
          return (
            <h2 key={index} className="text-xl font-semibold text-text-primary mt-6 mb-3">
              {paragraph?.replace('## ', '')}
            </h2>
          );
        }
        if (paragraph?.startsWith('### ')) {
          return (
            <h3 key={index} className="text-lg font-medium text-text-primary mt-4 mb-2">
              {paragraph?.replace('### ', '')}
            </h3>
          );
        }
        if (paragraph?.startsWith('**') && paragraph?.endsWith('**')) {
          return (
            <h4 key={index} className="font-medium text-text-primary mt-3 mb-2">
              {paragraph?.replace(/\*\*/g, '')}
            </h4>
          );
        }
        if (paragraph?.startsWith('*') && paragraph?.endsWith('*')) {
          return (
            <h5 key={index} className="font-medium text-text-secondary mt-2 mb-1 italic">
              {paragraph?.replace(/\*/g, '')}
            </h5>
          );
        }
        if (paragraph?.startsWith('- ')) {
          const items = paragraph?.split('\n')?.filter(item => item?.startsWith('- '));
          return (
            <ul key={index} className="list-disc list-inside space-y-1 mb-4 text-text-secondary">
              {items?.map((item, itemIndex) => (
                <li key={itemIndex}>{item?.replace('- ', '')}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="text-text-secondary mb-4 leading-relaxed">
            {paragraph}
          </p>
        );
      });
  };

  return (
    <div className="bg-white">
      {selectedArticle ? (
        /* Article Detail View */
        (<div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={closeArticleDetail}
              iconName="ArrowLeft"
              iconPosition="left"
              className="mb-4"
            >
              Back to Articles
            </Button>
            
            <div className="mb-6">
              <div className="h-64 md:h-80 rounded-lg overflow-hidden bg-muted mb-6">
                <Image
                  src={selectedArticle?.image}
                  alt={selectedArticle?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-text-secondary mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedArticle?.category === 'tips' ? 'bg-primary/10 text-primary' :
                  selectedArticle?.category === 'seasonal' ? 'bg-warning/10 text-warning' :
                  selectedArticle?.category === 'maintenance' ? 'bg-success/10 text-success' :
                  selectedArticle?.category === 'health'? 'bg-error/10 text-error' : 'bg-muted text-text-secondary'
                }`}>
                  {selectedArticle?.category}
                </span>
                <span>{formatDate(selectedArticle?.publishDate)}</span>
                <span>{selectedArticle?.readTime}</span>
                <span>By {selectedArticle?.author}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                {selectedArticle?.title}
              </h1>
              
              <p className="text-lg text-text-secondary leading-relaxed">
                {selectedArticle?.excerpt}
              </p>
            </div>
          </div>
          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <div className="text-text-secondary leading-relaxed">
              {formatContent(selectedArticle?.content)}
            </div>
          </div>
          {/* Article Tags */}
          <div className="mt-8 pt-6 border-t border-border">
            <h4 className="font-medium text-text-primary mb-3">Tags</h4>
            <div className="flex flex-wrap gap-2">
              {selectedArticle?.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-muted text-text-secondary text-sm rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>)
      ) : (
        /* Articles Grid View */
        (<div>
          {/* Search and Filter */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Search articles, tips, and guides..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div className="flex space-x-2 overflow-x-auto">
                {categories?.map((category) => (
                  <Button
                    key={category?.value}
                    variant={selectedCategory === category?.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category?.value)}
                    className="whitespace-nowrap"
                  >
                    {category?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles?.map((article) => (
              <article
                key={article?.id}
                className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-elevation-2 transition-all duration-200 cursor-pointer"
                onClick={() => handleArticleSelect(article)}
              >
                <div className="h-48 overflow-hidden bg-muted">
                  <Image
                    src={article?.image}
                    alt={article?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      article?.category === 'tips' ? 'bg-primary/10 text-primary' :
                      article?.category === 'seasonal' ? 'bg-warning/10 text-warning' :
                      article?.category === 'maintenance' ? 'bg-success/10 text-success' :
                      article?.category === 'health'? 'bg-error/10 text-error' : 'bg-muted text-text-secondary'
                    }`}>
                      {article?.category}
                    </span>
                    <span className="text-xs text-text-secondary">{article?.readTime}</span>
                  </div>
                  <h3 className="font-semibold text-text-primary mb-2 line-clamp-2">
                    {article?.title}
                  </h3>
                  <p className="text-sm text-text-secondary mb-3 line-clamp-3">
                    {article?.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-text-secondary">
                    <span>By {article?.author}</span>
                    <span>{formatDate(article?.publishDate)}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {filteredArticles?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Search" size={32} color="var(--color-text-secondary)" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">
                No articles found
              </h3>
              <p className="text-text-secondary">
                Try adjusting your search or filter criteria
              </p>
            </div>
          )}
        </div>)
      )}
    </div>
  );
};

export default KnowledgeBase;